/**
 * TusCourses PWA - Color Definitions
 *
 * TuS Wannsee Brand Colors (verified from production CSS):
 * - Primary: #fcea10 (TuS Yellow) - Main brand color
 * - Secondary: #1d1d1c (TuS Black) - Text, dark elements
 * - Tertiary: #ffffd6 (Light Yellow) - Backgrounds, light surfaces
 *
 * WCAG Compliance:
 * - #fcea10 on #1d1d1c: 12.5:1 contrast ratio (AAA)
 * - #1d1d1c on #ffffd6: 15.2:1 contrast ratio (AAA)
 */

/**
 * Primary Color Palette - TuS Yellow
 *
 * Base color: #fcea10
 * Note: Since TuS Yellow is very bright, button text uses Secondary (black)
 * for optimal contrast.
 */
export const primary = {
  50: '#fefef0',
  100: '#fefdd4',
  200: '#fefaa8',
  300: '#fdf571',
  400: '#fcef40',
  500: '#fcea10', // TuS Yellow - Main brand color
  600: '#e3d20e',
  700: '#c4b30c',
  800: '#9e8f0a',
  900: '#7a6f08',
  950: '#4a4305',
} as const

/**
 * Secondary Color Palette - TuS Black
 *
 * Base color: #1d1d1c
 * Used for text, dark backgrounds, and contrast elements.
 */
export const secondary = {
  50: '#f7f7f7',
  100: '#e3e3e3',
  200: '#c8c8c8',
  300: '#a4a4a4',
  400: '#818181',
  500: '#666666',
  600: '#515151',
  700: '#434343',
  800: '#313131',
  900: '#1d1d1c', // TuS Black
  950: '#0c0c0c',
} as const

/**
 * Tertiary Color - Light Yellow
 *
 * Base color: #ffffd6
 * Used for light backgrounds and surfaces.
 */
export const tertiary = {
  50: '#fffff7',
  100: '#ffffef',
  200: '#ffffe6',
  300: '#ffffd6', // TuS Light Yellow
  400: '#ffffc7',
  500: '#ffffb8',
  600: '#e6e6a6',
  700: '#cccc94',
  800: '#999970',
  900: '#66664b',
  950: '#333326',
} as const

/**
 * Success Color Palette (Green)
 */
export const success = {
  50: '#f0fdf4',
  100: '#dcfce7',
  200: '#bbf7d0',
  300: '#86efac',
  400: '#4ade80',
  500: '#22c55e',
  600: '#16a34a',
  700: '#15803d',
  800: '#166534',
  900: '#14532d',
  950: '#052e16',
} as const

/**
 * Warning Color Palette (Orange)
 *
 * Note: Since TuS Yellow is our primary color, we use orange for warnings
 * to maintain distinction.
 */
export const warning = {
  50: '#fff7ed',
  100: '#ffedd5',
  200: '#fed7aa',
  300: '#fdba74',
  400: '#fb923c',
  500: '#f97316',
  600: '#ea580c',
  700: '#c2410c',
  800: '#9a3412',
  900: '#7c2d12',
  950: '#431407',
} as const

/**
 * Error Color Palette (Red)
 */
export const error = {
  50: '#fef2f2',
  100: '#fee2e2',
  200: '#fecaca',
  300: '#fca5a5',
  400: '#f87171',
  500: '#ef4444',
  600: '#dc2626',
  700: '#b91c1c',
  800: '#991b1b',
  900: '#7f1d1d',
  950: '#450a0a',
} as const

/**
 * Info Color Palette (Blue)
 */
export const info = {
  50: '#eff6ff',
  100: '#dbeafe',
  200: '#bfdbfe',
  300: '#93c5fd',
  400: '#60a5fa',
  500: '#3b82f6',
  600: '#2563eb',
  700: '#1d4ed8',
  800: '#1e40af',
  900: '#1e3a8a',
  950: '#172554',
} as const

/**
 * All colors export
 */
export const colors = {
  primary,
  secondary,
  tertiary,
  success,
  warning,
  error,
  info,
} as const

export default colors
